import {
  SPREADSHEET_ENGINE_PROTOCOL_VERSION,
  buildSheetChunkId,
  buildSheetChunkKey,
  buildSpreadsheetStyleFromFormat,
  type SpreadsheetCellRecord,
  type SpreadsheetCellValue,
  type SpreadsheetRangeAddress,
  type SpreadsheetSheetSnapshot,
  type SpreadsheetWorkbookSnapshot,
} from '../../protocol/spreadsheetEngineProtocol'
import type {
  QuickBooksFpaStatementFormatting,
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
} from '../../data/ServerApiClient'
import {
  buildCfoStatementColumnSpecs,
  type CfoStatementColumnSpec,
  type CfoStatementColumnWindow,
  type CfoStatementComparisonMode,
} from './cfo/cfoStatementColumnSpecs'

export type CfoCoreExcelStatementType = 'profit_and_loss' | 'balance_sheet' | 'cash_flow'
export type CfoCoreExcelPeriodKind = 'actual' | 'forecast'

export type CfoCoreExcelView = {
  comparisonMode: CfoStatementComparisonMode
  endPeriod?: string | null
  maxPeriods?: number
  periodWindow?: CfoStatementColumnWindow
}

export type CfoCoreExcelExportInput = {
  companyName?: string | null
  currencyCode?: string | null
  asOfDate?: string | null
  grain?: string | null
  hideZeroRows?: boolean
  formatting?: QuickBooksFpaStatementFormatting | null
  view?: CfoCoreExcelView | null
  statements: Record<CfoCoreExcelStatementType, QuickBooksFpaStatementRender>
}

export type CfoCorePlanExcelAssumption = {
  key?: string | null
  label?: string | null
  source?: string | null
  confidence?: number | null
  value_json?: unknown
  citation_json?: unknown
  metadata?: unknown
  created_at?: string | null
  updated_at?: string | null
}

export type CfoCorePlanExcelExportInput = CfoCoreExcelExportInput & {
  planName?: string | null
  assumptions?: CfoCorePlanExcelAssumption[] | null
  periodKinds?: Partial<Record<CfoCoreExcelStatementType, CfoCoreExcelPeriodKind[]>>
}

const STATEMENT_SPECS: Array<{ type: CfoCoreExcelStatementType; sheetName: string; sourceSheetName: string; title: string; tabColor: string }> = [
  { type: 'profit_and_loss', sheetName: 'P&L', sourceSheetName: 'Source - P&L', title: 'Profit and Loss', tabColor: '#17803D' },
  { type: 'balance_sheet', sheetName: 'Balance Sheet', sourceSheetName: 'Source - Balance Sheet', title: 'Balance Sheet', tabColor: '#2563EB' },
  { type: 'cash_flow', sheetName: 'Cash Flow', sourceSheetName: 'Source - Cash Flow', title: 'Statement of Cash Flows', tabColor: '#7C3AED' },
]

const SOURCE_COLUMNS = [
  'node_id',
  'parent_node_id',
  'semantic_key',
  'row_type',
  'statement_type',
  'account_number',
  'account_id',
  'normal_balance',
  'level',
  'is_total',
  'is_formula',
  'label',
] as const

type WorkbookBuilder = {
  styles: Record<string, Record<string, unknown>>
}

type SheetCell = string | number | boolean | null | undefined | { formula: string; value?: number | string | boolean | null }

type GovernedSubtotalExpression = {
  expression_id: string
  operator: 'sum' | 'subtract' | 'cash_rollforward' | 'balance'
  operands: string[]
}

type GovernedStatementRow = QuickBooksFpaStatementRow & {
  governed_source_only?: boolean
  governed_subtotal_expression?: GovernedSubtotalExpression | null
}

type BuiltSheet = {
  sheet: SpreadsheetSheetSnapshot
  conditionalFormats?: Record<string, Record<string, unknown>>
}

type StatementBuildMeta = {
  sheetName: string
  sourceSheetName: string
  dataStartRow: number
  periodStartCol: number
  amountCols: number[]
  columnKinds: CfoStatementColumnSpec['kind'][]
  sourcePeriodStartCol: number
  divisor: number
  periods: string[]
  rowBySemantic: Map<string, number>
  rowByNodeId: Map<string, number>
  sourceIndexBySemantic: Map<string, number>
  sourceIndexByOutputRow: Map<number, number>
  displayedValuesBySemantic: Map<string, number[]>
  displayedValuesByOutputRow: Map<number, number[]>
  rows: QuickBooksFpaStatementRow[]
}

const escapeSheetName = (name: string): string => `'${String(name || '').replace(/'/g, "''")}'`

const excelCol = (zeroBasedCol: number): string => {
  let n = Math.max(0, Math.floor(zeroBasedCol)) + 1
  let out = ''
  while (n > 0) {
    const rem = (n - 1) % 26
    out = String.fromCharCode(65 + rem) + out
    n = Math.floor((n - 1) / 26)
  }
  return out
}

const excelRef = (row: number, col: number, absolute = false): string => {
  const marker = absolute ? '$' : ''
  return `${marker}${excelCol(col)}${marker}${row + 1}`
}

const formulaRef = (sheetName: string, row: number, col: number): string => `${escapeSheetName(sheetName)}!${excelRef(row, col)}`

const CFO_SHEET_ID_BY_NAME: Record<string, string> = {
  'P&L': 'sheet_1',
  'Balance Sheet': 'sheet_2',
  'Cash Flow': 'sheet_3',
  'Source - P&L': 'sheet_4',
  'Source - Balance Sheet': 'sheet_5',
  'Source - Cash Flow': 'sheet_6',
  Checks: 'sheet_7',
}

const cellValue = (value: SheetCell): SpreadsheetCellValue => {
  if (value && typeof value === 'object' && 'formula' in value) {
    return cellValue(value.value ?? null)
  }
  if (typeof value === 'number' && Number.isFinite(value)) return { kind: 'number', value }
  if (typeof value === 'boolean') return { kind: 'boolean', value }
  if (typeof value === 'string') return { kind: 'text', value }
  return { kind: 'blank' }
}

const cellRawInput = (value: SheetCell): string => {
  if (value && typeof value === 'object' && 'formula' in value) return value.formula
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE'
  if (typeof value === 'string') return value
  return ''
}

const normalizeFormatting = (formatting?: QuickBooksFpaStatementFormatting | null): Required<QuickBooksFpaStatementFormatting> => ({
  theme: formatting?.theme || 'classic',
  scale: formatting?.scale || 'ones',
  decimals: Number.isFinite(Number(formatting?.decimals)) ? Number(formatting?.decimals) : 0,
  negativeFormat: formatting?.negativeFormat || 'parentheses',
  zeroDisplay: formatting?.zeroDisplay || 'dash',
  showAccountCodes: Boolean(formatting?.showAccountCodes),
  showDetailRows: formatting?.showDetailRows !== false,
  showHeaders: formatting?.showHeaders !== false,
  showPercentOfSales: Boolean(formatting?.showPercentOfSales),
  showTotalColumn: formatting?.showTotalColumn !== false,
})

const scaleDivisor = (formatting: Required<QuickBooksFpaStatementFormatting>): number => {
  if (formatting.scale === 'millions') return 1_000_000
  if (formatting.scale === 'thousands') return 1_000
  return 1
}

const amountFormatCode = (formatting: Required<QuickBooksFpaStatementFormatting>): string => {
  const decimals = Math.max(0, Math.min(4, Math.floor(Number(formatting.decimals) || 0)))
  const places = decimals ? `.${'0'.repeat(decimals)}` : ''
  const positive = `#,##0${places}`
  const negative = formatting.negativeFormat === 'minus' ? `-${positive}` : `(${positive})`
  if (formatting.zeroDisplay === 'blank') return `${positive};${negative};`
  if (formatting.zeroDisplay === 'zero') return `${positive};${negative};0${places}`
  return `${positive};${negative};-`
}

const percentFormatCode = (formatting: Required<QuickBooksFpaStatementFormatting>): string => {
  const decimals = Math.max(0, Math.min(2, Math.floor(Number(formatting.decimals) || 0)))
  return `0${decimals ? `.${'0'.repeat(decimals)}` : ''}%`
}

const addStyle = (builder: WorkbookBuilder, id: string, format: Record<string, unknown>): string => {
  const built = buildSpreadsheetStyleFromFormat(format)
  if (built.style) builder.styles[id] = built.style
  return id
}

const makeCellRecord = (value: SheetCell, styleId?: string | null): SpreadsheetCellRecord => ({
  raw_input: cellRawInput(value),
  computed_value: cellValue(value),
  style_id: styleId || null,
  revision: 1,
})

const buildChunks = (
  sheetId: string,
  rows: SheetCell[][],
  styleForCell: (row: number, col: number, value: SheetCell) => string | null | undefined,
): Record<string, Record<string, SpreadsheetCellRecord>> => {
  const chunks: Record<string, Record<string, SpreadsheetCellRecord>> = {}
  rows.forEach((rowValues, row) => {
    rowValues.forEach((value, col) => {
      if (value == null || value === '') return
      const key = buildSheetChunkKey(sheetId, row, col)
      const chunkId = buildSheetChunkId(key)
      chunks[chunkId] ??= {}
      chunks[chunkId][`${row}:${col}`] = makeCellRecord(value, styleForCell(row, col, value))
    })
  })
  return chunks
}

const cleanSheetId = (name: string): string => {
  const known = CFO_SHEET_ID_BY_NAME[name]
  if (known) return known
  let hash = 2166136261
  for (let index = 0; index < name.length; index += 1) {
    hash ^= name.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return `sheet_${8 + (hash >>> 0)}`
}

const formatPeriodLabel = (period: string): string => {
  const date = new Date(`${period}T00:00:00`)
  if (Number.isNaN(date.getTime())) return String(period || '')
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' })
}

const scaleLabel = (formatting: Required<QuickBooksFpaStatementFormatting>, currencyCode?: string | null): string => {
  const currency = String(currencyCode || '').trim().toUpperCase() || '$'
  if (formatting.scale === 'millions') return `${currency} in millions`
  if (formatting.scale === 'thousands') return `${currency} in thousands`
  return currency
}

const statementRowValues = (row: QuickBooksFpaStatementRow): number[] =>
  Array.isArray(row.values) ? row.values.map((value) => Number.isFinite(Number(value)) ? Number(value) : 0) : []

const rowHasMaterialValue = (row: QuickBooksFpaStatementRow): boolean =>
  statementRowValues(row).some((value) => Math.abs(value) > 0.000001)

const filterZeroStatementRows = (rows: QuickBooksFpaStatementRow[], hideZeroRows?: boolean): QuickBooksFpaStatementRow[] => {
  if (!hideZeroRows) return rows
  const requiredControlSemantics = new Set([
    'beginning_cash',
    'net_change_cash',
    'ending_cash',
    'total_assets',
    'total_liabilities_equity',
  ])
  const keep = new Array(rows.length).fill(false)
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const row = rows[index]
    const rowType = String(row?.row_type || '').trim()
    if (rowType === 'spacer') {
      keep[index] = false
      continue
    }
    const children = immediateChildIndexes(rows, index)
    keep[index] = requiredControlSemantics.has(semanticKey(row))
      || rowHasMaterialValue(row)
      || children.some((childIndex) => keep[childIndex])
  }
  return rows.filter((_, index) => keep[index])
}

const rowLabel = (row: QuickBooksFpaStatementRow, formatting: Required<QuickBooksFpaStatementFormatting>): string => {
  // Internal/provider source IDs are identity keys, not human-readable account codes.
  // Only render the provider's actual account number when one is available.
  const code = formatting.showAccountCodes ? String(row.account_number || '').trim() : ''
  const label = String(row.label || '').trim()
  return code ? `${code} ${label}` : label
}

const isBalanceSheetCashRow = (row: QuickBooksFpaStatementRow): boolean => {
  if (row.statement_type !== 'balance_sheet') return false
  const cashFlowSection = String((row as any).cash_flow_section || '').trim().toLowerCase()
  if (cashFlowSection === 'cash') return true
  const accountType = String((row as any).qbo_account_type || '').trim().toLowerCase().replace(/[\s_-]+/g, '')
  const accountSubType = String((row as any).qbo_account_sub_type || '').trim().toLowerCase().replace(/[\s_-]+/g, '')
  const label = String(row.label || '').trim().toLowerCase()
  if (accountType === 'bank') return true
  if (accountSubType.includes('cash') || accountSubType.includes('checking') || accountSubType.includes('savings')) return true
  return /\b(cash|checking|savings)\b/.test(label)
}

export const semanticKey = (row: QuickBooksFpaStatementRow): string => {
  const existing = String((row as any).semantic_key || '').trim()
  if (existing) return existing
  const label = String(row.label || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
  if (/^net income$|^net ordinary income$|^net earnings$/.test(label)) return 'net_income'
  if (/^ebitda$/.test(label)) return 'ebitda'
  if (/^ebitda adjustments$/.test(label)) return 'ebitda_adjustments'
  if (/^total ebitda adjustments$/.test(label)) return 'total_ebitda_adjustments'
  if (/^gross profit$/.test(label)) return 'gross_profit'
  if (/^net operating income$/.test(label)) return 'net_operating_income'
  if (/^net other income$/.test(label)) return 'net_other_income'
  if (/^total (revenue|income|sales)( accounts?)?$/.test(label)) return 'total_revenue'
  if (/^total cost of sales( accounts?)?$|^total cost of goods sold( accounts?)?$|^total cogs( accounts?)?$/.test(label)) return 'total_cost_of_sales'
  if (/^total (expense|expenses|operating expenses)( accounts?)?$/.test(label)) return 'total_expenses'
  if (/^total other income( accounts?)?$/.test(label)) return 'total_other_income'
  if (/^total other expenses?( accounts?)?$/.test(label)) return 'total_other_expenses'
  if (/^total current assets$/.test(label)) return 'total_current_assets'
  if (/^total fixed assets$/.test(label)) return 'total_fixed_assets'
  if (/^total other assets$/.test(label)) return 'total_other_assets'
  if (/^total assets$/.test(label)) return 'total_assets'
  if (/^total current liabilities$/.test(label)) return 'total_current_liabilities'
  if (/^total (long term|long-term) liabilities$/.test(label)) return 'total_long_term_liabilities'
  if (/^total liabilities$/.test(label)) return 'total_liabilities'
  if (/^total equity$/.test(label)) return 'total_equity'
  if (/^total liabilities equity$|^total liabilities (and|&) equity$|^total liabilities and shareholders equity$/.test(label)) return 'total_liabilities_equity'
  if (/^beginning cash/.test(label)) return 'beginning_cash'
  if (/^ending cash/.test(label)) return 'ending_cash'
  if (/^net change in cash/.test(label)) return 'net_change_cash'
  if (/^net cash .*operating/.test(label)) return 'net_cash_operating'
  if (/^net cash .*investing/.test(label)) return 'net_cash_investing'
  if (/^net cash .*financing/.test(label)) return 'net_cash_financing'
  if (isBalanceSheetCashRow(row)) return 'cash'
  return ''
}

const immediateChildIndexes = (rows: QuickBooksFpaStatementRow[], index: number): number[] => {
  const row = rows[index]
  const nodeId = String(row?.node_id || '').trim()
  if (nodeId) {
    const byParent = rows
      .map((candidate, candidateIndex) => ({ candidate, candidateIndex }))
      .filter(({ candidate }) => String(candidate.parent_node_id || '').trim() === nodeId)
      .map(({ candidateIndex }) => candidateIndex)
    if (byParent.length) return byParent
  }
  const level = Math.max(0, Math.floor(Number(row?.level || 0)))
  const out: number[] = []
  for (let cursor = index + 1; cursor < rows.length; cursor += 1) {
    const nextLevel = Math.max(0, Math.floor(Number(rows[cursor]?.level || 0)))
    if (nextLevel <= level) break
    if (nextLevel === level + 1) out.push(cursor)
  }
  return out
}

const isHeadingLikeRow = (row: QuickBooksFpaStatementRow | undefined): boolean => {
  const rowType = String(row?.row_type || '').trim()
  return rowType === 'heading' || rowType === 'spacer'
}

const isGeneratedTotalForParent = (row: QuickBooksFpaStatementRow | undefined, parentNodeId: string): boolean => {
  if (!row || !parentNodeId) return false
  const nodeId = String(row.node_id || '').trim()
  if (nodeId === `${parentNodeId}:total` || nodeId === `${parentNodeId}:subtotal`) return true
  return String(row.row_type || '').trim() === 'group' && /^total\b/i.test(String(row.label || '').trim())
}

const summableChildIndexes = (rows: QuickBooksFpaStatementRow[], index: number): number[] => {
  const parentNodeId = String(rows[index]?.node_id || '').trim()
  return immediateChildIndexes(rows, index)
    .filter((childIndex) => !isHeadingLikeRow(rows[childIndex]))
    .filter((childIndex) => !isGeneratedTotalForParent(rows[childIndex], parentNodeId))
}

const subtotalBaseNodeId = (row: QuickBooksFpaStatementRow | undefined): string => {
  const nodeId = String(row?.node_id || '').trim()
  for (const suffix of [':subtotal', ':total']) {
    if (nodeId.endsWith(suffix)) return nodeId.slice(0, -suffix.length)
  }
  return ''
}

const subtotalSourceIndexes = (rows: QuickBooksFpaStatementRow[], index: number): number[] => {
  const baseNodeId = subtotalBaseNodeId(rows[index])
  if (baseNodeId) {
    const baseIndex = rows.findIndex((candidate) => String(candidate.node_id || '').trim() === baseNodeId)
    const baseRow = baseIndex >= 0 ? rows[baseIndex] : null
    if (baseRow && String(baseRow.row_type || '').trim() === 'account' && String(baseRow.account_id || '').trim()) {
      return [baseIndex]
    }
    const byParent = rows
      .map((candidate, candidateIndex) => ({ candidate, candidateIndex }))
      .filter(({ candidateIndex }) => candidateIndex !== index)
      .filter(({ candidate }) => String(candidate.parent_node_id || '').trim() === baseNodeId)
      .filter(({ candidate }) => !isHeadingLikeRow(candidate))
      .filter(({ candidate }) => !isGeneratedTotalForParent(candidate, baseNodeId))
      .map(({ candidateIndex }) => candidateIndex)
    if (byParent.length) return byParent
  }
  const level = Math.max(0, Math.floor(Number(rows[index]?.level || 0)))
  const out: number[] = []
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const row = rows[cursor]
    const rowLevel = Math.max(0, Math.floor(Number(row?.level || 0)))
    if (rowLevel < level) break
    if (rowLevel === level) break
    if (rowLevel !== level + 1) continue
    if (!isHeadingLikeRow(row)) out.unshift(cursor)
  }
  const subtotalBaseNodeIds = new Set(out.map((candidateIndex) => subtotalBaseNodeId(rows[candidateIndex])).filter(Boolean))
  return subtotalBaseNodeIds.size
    ? out.filter((candidateIndex) => !subtotalBaseNodeIds.has(String(rows[candidateIndex]?.node_id || '').trim()))
    : out
}

const sumExpression = (refs: string[]): string => {
  if (!refs.length) return '0'
  if (refs.length === 1) return refs[0]
  return `SUM(${refs.join(',')})`
}

const sumRefs = (refs: string[]): string => `=${sumExpression(refs)}`

const expressionOrZero = (ref: string | null | undefined): string => ref || '0'

const addSubtractExpression = (positiveRefs: Array<string | null | undefined>, negativeRefs: Array<string | null | undefined> = []): string | null => {
  const positives = positiveRefs.filter((ref): ref is string => Boolean(ref))
  const negatives = negativeRefs.filter((ref): ref is string => Boolean(ref))
  if (!positives.length && !negatives.length) return null
  const positiveExpression = positives.length ? sumExpression(positives) : '0'
  if (!negatives.length) return positiveExpression
  return `${positiveExpression}-${sumExpression(negatives)}`
}

const buildSourceSheet = (
  spec: typeof STATEMENT_SPECS[number],
  render: QuickBooksFpaStatementRender,
): BuiltSheet => {
  const sheetId = cleanSheetId(spec.sourceSheetName)
  const periods = Array.isArray(render.periods) ? render.periods : []
  const headers = [...SOURCE_COLUMNS, ...periods]
  const rows: SheetCell[][] = [headers]
  for (const row of render.rows || []) {
    rows.push([
      row.node_id || '',
      row.parent_node_id || '',
      semanticKey(row),
      row.row_type || '',
      row.statement_type || spec.type,
      row.account_number || '',
      row.account_id || '',
      row.normal_balance || '',
      Number.isFinite(Number(row.level)) ? Number(row.level) : 0,
      Boolean(row.is_total),
      Boolean(row.is_formula),
      row.label || '',
      ...periods.map((_, index) => statementRowValues(row)[index] ?? 0),
    ])
  }
  const rowCount = Math.max(rows.length, 25)
  const colCount = Math.max(headers.length, 16)
  const sheet: SpreadsheetSheetSnapshot = {
    sheet_id: sheetId,
    name: spec.sourceSheetName,
    row_count: rowCount,
    col_count: colCount,
    hidden: true,
    chunks: buildChunks(sheetId, rows, (row, col) => {
      if (row === 0) return 'source_header'
      if (col >= SOURCE_COLUMNS.length) return 'source_amount'
      return 'source_text'
    }),
    frozen_panes: { rows: 1, cols: SOURCE_COLUMNS.length },
    tab_color: { rgb: spec.tabColor },
    column_metadata: Object.fromEntries(headers.map((header, index) => [
      String(index),
      { width_px: index < SOURCE_COLUMNS.length ? Math.max(85, Math.min(160, String(header).length * 9)) : 92 },
    ])),
    sheet_format_properties: {
      default_row_height_px: 22,
      default_col_width_px: 92,
      show_outline_symbols: true,
    },
    auto_filter: {
      range: { sheet_id: sheetId, start_row: 0, start_col: 0, end_row: Math.max(0, rows.length - 1), end_col: Math.max(0, headers.length - 1) },
    },
    print_settings: buildPrintSettings(spec.title),
  }
  return { sheet }
}

const buildStatementSheet = (
  builder: WorkbookBuilder,
  spec: typeof STATEMENT_SPECS[number],
  render: QuickBooksFpaStatementRender,
  formatting: Required<QuickBooksFpaStatementFormatting>,
  hideZeroRows: boolean | undefined,
  args: {
    companyName?: string | null
    currencyCode?: string | null
    asOfDate?: string | null
    grain?: string | null
    periodKinds?: CfoCoreExcelPeriodKind[] | null
    editableForecastCells?: boolean
    view?: CfoCoreExcelView | null
  },
): { built: BuiltSheet; meta: StatementBuildMeta } => {
  const sheetId = cleanSheetId(spec.sheetName)
  const rows = filterZeroStatementRows(Array.isArray(render.rows) ? render.rows : [], hideZeroRows)
  const periods = Array.isArray(render.periods) ? render.periods : []
  const columnSpecs = args.view
    ? buildCfoStatementColumnSpecs(
        render,
        args.view.endPeriod || args.asOfDate || periods[periods.length - 1] || null,
        args.view.comparisonMode,
        Math.max(1, Number(args.view.maxPeriods) || 12),
        args.view.periodWindow,
      )
    : periods.map((period, index): CfoStatementColumnSpec => ({
        key: `period:${period}:${index}`,
        label: formatPeriodLabel(period),
        kind: 'value',
        period,
        periodIndex: index,
        indices: [index],
      }))
  const divisor = scaleDivisor(formatting)
  const includePercent = !args.view && spec.type === 'profit_and_loss' && Boolean(formatting.showPercentOfSales)
  const includeTotal = !args.view && Boolean(formatting.showTotalColumn)
  const periodStartCol = 1
  const dataStartRow = formatting.showHeaders ? 5 : 1
  const sourcePeriodStartCol = SOURCE_COLUMNS.length
  const totalCol = periodStartCol + columnSpecs.length * (includePercent ? 2 : 1)
  const colCount = Math.max(totalCol + (includeTotal ? 1 : 0), 8)
  const out: SheetCell[][] = []
  if (formatting.showHeaders) {
    out.push([args.companyName || 'Company', ...new Array(colCount - 1).fill(null)])
    out.push([spec.title, ...new Array(colCount - 1).fill(null)])
    out.push([[
      args.view
        ? `Excel view: ${columnSpecs.map((column) => column.label).join(' | ')}`
        : periods.length ? `${formatPeriodLabel(periods[0])} - ${formatPeriodLabel(periods[periods.length - 1])}` : '',
      scaleLabel(formatting, args.currencyCode),
      String(args.grain || 'monthly'),
      args.asOfDate ? `As of ${args.asOfDate}` : '',
    ].filter(Boolean).join(' | '), ...new Array(colCount - 1).fill(null)])
    out.push(new Array(colCount).fill(null))
  }
  const header: SheetCell[] = ['Account']
  for (const column of columnSpecs) {
    header.push(column.label)
    if (includePercent) header.push('% Sales')
  }
  if (includeTotal) header.push(spec.type === 'balance_sheet' ? 'Ending' : 'Total')
  out.push(header)

  const outputRowBySourceIndex = new Map<number, number>()
  rows.forEach((_, index) => outputRowBySourceIndex.set(index, dataStartRow + index))
  const sourceIndexByNodeId = new Map<string, number>()
  rows.forEach((row, sourceIndex) => {
    const nodeId = String(row.node_id || '').trim()
    if (nodeId) sourceIndexByNodeId.set(nodeId, sourceIndex)
  })
  const revenueIndex = rows.findIndex((row) => semanticKey(row) === 'total_revenue')
  const semanticRows = new Map<string, number>()
  const semanticSourceIndexes = new Map<string, number>()
  const displayedValuesBySemantic = new Map<string, number[]>()
  const displayedValuesByOutputRow = new Map<number, number[]>()
  const nodeRows = new Map<string, number>()
  rows.forEach((row, sourceIndex) => {
    const semantic = semanticKey(row)
    if (semantic && !semanticSourceIndexes.has(semantic)) semanticSourceIndexes.set(semantic, sourceIndex)
  })
  const rowRefForSourceIndex = (sourceIndex: number | undefined, columnIndex: number): string | null => {
    if (sourceIndex == null) return null
    const outputRow = outputRowBySourceIndex.get(sourceIndex)
    if (outputRow == null) return null
    return excelRef(outputRow, periodStartCol + columnIndex * (includePercent ? 2 : 1))
  }
  const aggregateSourceValue = (sourceIndex: number | undefined, indices: number[]): number | null => {
    if (sourceIndex == null) return null
    const values = statementRowValues(rows[sourceIndex])
    const usable = indices.filter((index) => index >= 0 && index < values.length)
    if (!usable.length) return null
    if (spec.type === 'balance_sheet') return Number(values[usable[usable.length - 1]] || 0)
    return usable.reduce((sum, index) => sum + Number(values[index] || 0), 0)
  }
  const sourceValueAt = (sourceIndex: number | undefined, columnIndex: number): number | null => {
    const column = columnSpecs[columnIndex]
    if (!column) return null
    if (column.kind === 'value') return aggregateSourceValue(sourceIndex, column.indices || [])
    const current = aggregateSourceValue(sourceIndex, column.currentIndices || [])
    const prior = aggregateSourceValue(sourceIndex, column.priorIndices || [])
    if (current == null || prior == null) return null
    if (column.kind === 'pct') return Math.abs(prior) < 0.005 ? null : (current - prior) / Math.abs(prior)
    return current - prior
  }
  const rowRefForSemantic = (semantic: string, columnIndex: number): string | null => (
    rowRefForSourceIndex(semanticSourceIndexes.get(semantic), columnIndex)
  )
  const semanticValue = (semantic: string, columnIndex: number): number | null => (
    sourceValueAt(semanticSourceIndexes.get(semantic), columnIndex)
  )
  const sumSemanticValues = (semantics: string[], columnIndex: number): number | null => {
    let found = false
    const total = semantics.reduce((sum, semantic) => {
      const value = semanticValue(semantic, columnIndex)
      if (value == null) return sum
      found = true
      return sum + value
    }, 0)
    return found ? total : null
  }
  const equityHeadingIndex = rows.findIndex((row) => (
    String(row.row_type || '').trim() === 'heading' && /^equity$/i.test(String(row.label || '').trim())
  ))
  const equityComponentIndexes = equityHeadingIndex >= 0 ? summableChildIndexes(rows, equityHeadingIndex) : []
  const valueForSemanticFormula = (semantic: string, columnIndex: number): number | null => {
    if (!semantic) return null
    if (spec.type === 'profit_and_loss') {
      if (semantic === 'gross_profit') {
        const revenue = semanticValue('total_revenue', columnIndex)
        const costOfSales = semanticValue('total_cost_of_sales', columnIndex)
        return revenue == null && costOfSales == null ? null : (revenue || 0) - (costOfSales || 0)
      }
      if (semantic === 'net_operating_income') {
        const grossProfit = semanticValue('gross_profit', columnIndex)
        const expenses = semanticValue('total_expenses', columnIndex)
        return grossProfit == null && expenses == null ? null : (grossProfit || 0) - (expenses || 0)
      }
      if (semantic === 'net_other_income') {
        const otherIncome = semanticValue('total_other_income', columnIndex)
        const otherExpenses = semanticValue('total_other_expenses', columnIndex)
        return otherIncome == null && otherExpenses == null ? null : (otherIncome || 0) - (otherExpenses || 0)
      }
      if (semantic === 'net_income') {
        const operating = semanticValue('net_operating_income', columnIndex)
        const other = semanticValue('net_other_income', columnIndex)
        if (operating != null || other != null) return (operating || 0) + (other || 0)
        const grossProfit = semanticValue('gross_profit', columnIndex)
        const otherIncome = semanticValue('total_other_income', columnIndex)
        const expenses = semanticValue('total_expenses', columnIndex)
        const otherExpenses = semanticValue('total_other_expenses', columnIndex)
        return grossProfit == null && otherIncome == null && expenses == null && otherExpenses == null
          ? null
          : (grossProfit || 0) + (otherIncome || 0) - (expenses || 0) - (otherExpenses || 0)
      }
      if (semantic === 'ebitda') {
        const netIncome = semanticValue('net_income', columnIndex)
        const addbacks = semanticValue('total_ebitda_adjustments', columnIndex) ?? semanticValue('ebitda_adjustments', columnIndex)
        return netIncome == null && addbacks == null ? null : (netIncome || 0) + (addbacks || 0)
      }
    }
    if (spec.type === 'balance_sheet') {
      if (semantic === 'total_assets') return sumSemanticValues(['total_current_assets', 'total_fixed_assets', 'total_other_assets'], columnIndex)
      if (semantic === 'total_liabilities') return sumSemanticValues(['total_current_liabilities', 'total_long_term_liabilities'], columnIndex)
      if (semantic === 'total_equity' && equityComponentIndexes.length) {
        return equityComponentIndexes.reduce((sum, index) => sum + (sourceValueAt(index, columnIndex) || 0), 0)
      }
      if (semantic === 'total_liabilities_equity') return sumSemanticValues(['total_liabilities', 'total_equity'], columnIndex)
    }
    if (spec.type === 'cash_flow') {
      if (semantic === 'net_change_cash') return sumSemanticValues(['net_cash_operating', 'net_cash_investing', 'net_cash_financing'], columnIndex)
      if (semantic === 'ending_cash') return sumSemanticValues(['beginning_cash', 'net_change_cash'], columnIndex)
    }
    return null
  }
  const formulaForSemantic = (semantic: string, columnIndex: number): string | null => {
    if (!semantic) return null
    if (spec.type === 'profit_and_loss') {
      if (semantic === 'gross_profit') {
        const expression = addSubtractExpression(
          [rowRefForSemantic('total_revenue', columnIndex)],
          [rowRefForSemantic('total_cost_of_sales', columnIndex)],
        )
        return expression ? `=${expression}` : null
      }
      if (semantic === 'net_operating_income') {
        const expression = addSubtractExpression(
          [rowRefForSemantic('gross_profit', columnIndex)],
          [rowRefForSemantic('total_expenses', columnIndex)],
        )
        return expression ? `=${expression}` : null
      }
      if (semantic === 'net_other_income') {
        const expression = addSubtractExpression(
          [rowRefForSemantic('total_other_income', columnIndex)],
          [rowRefForSemantic('total_other_expenses', columnIndex)],
        )
        return expression ? `=${expression}` : null
      }
      if (semantic === 'net_income') {
        const netOperating = rowRefForSemantic('net_operating_income', columnIndex)
        const netOther = rowRefForSemantic('net_other_income', columnIndex)
        if (netOperating || netOther) return `=${sumExpression([expressionOrZero(netOperating), expressionOrZero(netOther)])}`
        const grossProfit = rowRefForSemantic('gross_profit', columnIndex)
        const expression = addSubtractExpression(
          [grossProfit, rowRefForSemantic('total_other_income', columnIndex)],
          [rowRefForSemantic('total_expenses', columnIndex), rowRefForSemantic('total_other_expenses', columnIndex)],
        )
        return expression ? `=${expression}` : null
      }
      if (semantic === 'ebitda') {
        const addbacks = rowRefForSemantic('total_ebitda_adjustments', columnIndex) || rowRefForSemantic('ebitda_adjustments', columnIndex)
        const expression = addSubtractExpression([rowRefForSemantic('net_income', columnIndex), addbacks])
        return expression ? `=${expression}` : null
      }
    }
    if (spec.type === 'balance_sheet') {
      if (semantic === 'total_assets') {
        const expression = addSubtractExpression([
          rowRefForSemantic('total_current_assets', columnIndex),
          rowRefForSemantic('total_fixed_assets', columnIndex),
          rowRefForSemantic('total_other_assets', columnIndex),
        ])
        return expression ? `=${expression}` : null
      }
      if (semantic === 'total_liabilities') {
        const expression = addSubtractExpression([
          rowRefForSemantic('total_current_liabilities', columnIndex),
          rowRefForSemantic('total_long_term_liabilities', columnIndex),
        ])
        return expression ? `=${expression}` : null
      }
      if (semantic === 'total_equity' && equityComponentIndexes.length) {
        const refs = equityComponentIndexes
          .map((index) => rowRefForSourceIndex(index, columnIndex))
          .filter((ref): ref is string => Boolean(ref))
        return refs.length ? sumRefs(refs) : null
      }
      if (semantic === 'total_liabilities_equity') {
        const expression = addSubtractExpression([
          rowRefForSemantic('total_liabilities', columnIndex),
          rowRefForSemantic('total_equity', columnIndex),
        ])
        return expression ? `=${expression}` : null
      }
    }
    if (spec.type === 'cash_flow') {
      if (semantic === 'net_change_cash') {
        const expression = addSubtractExpression([
          rowRefForSemantic('net_cash_operating', columnIndex),
          rowRefForSemantic('net_cash_investing', columnIndex),
          rowRefForSemantic('net_cash_financing', columnIndex),
        ])
        return expression ? `=${expression}` : null
      }
      if (semantic === 'ending_cash') {
        const expression = addSubtractExpression([
          rowRefForSemantic('beginning_cash', columnIndex),
          rowRefForSemantic('net_change_cash', columnIndex),
        ])
        return expression ? `=${expression}` : null
      }
    }
    return null
  }
  const isForecastPeriod = (periodIndex: number): boolean => args.periodKinds?.[periodIndex] === 'forecast'
  const isEditableForecastLeaf = (
    row: QuickBooksFpaStatementRow,
    sourceIndex: number,
    formulaIndexes: number[],
    periodIndex: number,
  ): boolean => {
    if (!args.editableForecastCells || !isForecastPeriod(periodIndex)) return false
    const rowType = String(row.row_type || '').trim()
    if (row.is_total || row.is_formula || rowType === 'group' || rowType === 'formula' || rowType === 'heading' || rowType === 'spacer') return false
    if (/^total\b/i.test(String(row.label || '').trim())) return false
    if (formulaIndexes.length) return false
    return sourceIndex >= 0
  }
  const collectAssetLeafIndexesForCashPlug = (rootIndex: number, currentCashIndex: number, visited = new Set<number>()): number[] => {
    if (visited.has(rootIndex)) return []
    visited.add(rootIndex)
    const row = rows[rootIndex]
    if (!row || isHeadingLikeRow(row)) return []
    const children = subtotalBaseNodeId(row) ? subtotalSourceIndexes(rows, rootIndex) : summableChildIndexes(rows, rootIndex)
    if (children.length) {
      return children.flatMap((childIndex) => collectAssetLeafIndexesForCashPlug(childIndex, currentCashIndex, visited))
    }
    if (rootIndex === currentCashIndex) return []
    return [rootIndex]
  }
  const cashPlugSourceIndex = spec.type === 'balance_sheet' ? rows.findIndex((row) => isBalanceSheetCashRow(row)) : -1
  const cashPlugFormula = (sourceIndex: number, periodIndex: number): { formula: string; value: number } | null => {
    if (!args.editableForecastCells || spec.type !== 'balance_sheet' || !isForecastPeriod(periodIndex)) return null
    if (!isBalanceSheetCashRow(rows[sourceIndex])) return null
    if (sourceIndex !== cashPlugSourceIndex) return null
    const totalAssetsIndex = semanticSourceIndexes.get('total_assets')
    const liabilitiesEquityRef = rowRefForSemantic('total_liabilities_equity', periodIndex)
    if (totalAssetsIndex == null || !liabilitiesEquityRef) return null
    const otherAssetIndexes = collectAssetLeafIndexesForCashPlug(totalAssetsIndex, sourceIndex)
    const otherAssetRefs = otherAssetIndexes
      .map((index) => rowRefForSourceIndex(index, periodIndex))
      .filter((ref): ref is string => Boolean(ref))
    const liabilitiesEquityValue = semanticValue('total_liabilities_equity', periodIndex) || 0
    const otherAssetValue = otherAssetIndexes.reduce((sum, index) => sum + (sourceValueAt(index, periodIndex) || 0), 0)
    return {
      formula: `=${liabilitiesEquityRef}-${sumExpression(otherAssetRefs)}`,
      value: (liabilitiesEquityValue - otherAssetValue) / divisor,
    }
  }
  const sameIndices = (left: number[] | undefined, right: number[] | undefined) => (
    (left || []).length === (right || []).length && (left || []).every((value, index) => value === (right || [])[index])
  )
  const valueColumnIndexFor = (indices: number[] | undefined): number => columnSpecs.findIndex((column) => (
    column.kind === 'value' && sameIndices(column.indices, indices)
  ))
  const sourceFormulaForColumn = (sourceRow: number, column: CfoStatementColumnSpec): string => {
    const indices = (column.indices || []).filter((index) => index >= 0 && index < periods.length)
    const refs = indices.map((index) => formulaRef(spec.sourceSheetName, sourceRow, sourcePeriodStartCol + index))
    const expression = spec.type === 'balance_sheet'
      ? refs[refs.length - 1] || '0'
      : sumExpression(refs)
    return divisor === 1 ? `=${expression}` : `=${expression}/${divisor}`
  }
  const governedFormulaForColumn = (
    row: GovernedStatementRow,
    columnIndex: number,
    sourceValue: number,
  ): { formula: string; value: number } | null => {
    const expression = row.governed_subtotal_expression
    if (!expression) return null
    const refs = expression.operands.map((operand) => {
      const sourceIndex = sourceIndexByNodeId.get(operand)
      const ref = rowRefForSourceIndex(sourceIndex, columnIndex)
      if (!ref) throw new Error(`governed_formula_unknown_operand:${row.node_id || ''}:${operand}`)
      return ref
    })
    let formula: string
    switch (expression.operator) {
      case 'sum':
      case 'cash_rollforward':
        formula = refs.length === 1 ? `=${refs[0]}` : `=SUM(${refs.join(',')})`
        break
      case 'subtract':
        if (refs.length !== 2) throw new Error(`governed_formula_invalid_subtract:${row.node_id || ''}`)
        formula = `=${refs[0]}-${refs[1]}`
        break
      default:
        throw new Error(`governed_formula_unsupported_operator:${row.node_id || ''}:${expression.operator}`)
    }
    return { formula, value: sourceValue / divisor }
  }

  rows.forEach((row, sourceIndex) => {
    const outputRow = dataStartRow + sourceIndex
    const rowType = String(row.row_type || '').trim()
    const governedSourceOnly = Boolean((row as QuickBooksFpaStatementRow & { governed_source_only?: boolean }).governed_source_only)
    const valuesBlank = rowType === 'spacer' || (rowType === 'heading' && !rowHasMaterialValue(row))
    const semantic = semanticKey(row)
    const rowFormulaRefs = columnSpecs.map((column, columnIndex) => {
      const sourceRow = sourceIndex + 1
      const sourceValue = sourceValueAt(sourceIndex, columnIndex) ?? 0
      const childIndexes = summableChildIndexes(rows, sourceIndex)
      const subtotalIndexes = row.is_total ? subtotalSourceIndexes(rows, sourceIndex) : []
      const rowLooksLikeGeneratedTotal = String(row.row_type || '').trim() === 'group' || /^total\b/i.test(String(row.label || '').trim()) || Boolean(subtotalBaseNodeId(row))
      const formulaIndexes = rowLooksLikeGeneratedTotal && subtotalIndexes.length ? subtotalIndexes : childIndexes
      const childRefs = formulaIndexes
        .map((childIndex) => outputRowBySourceIndex.get(childIndex))
        .filter((childRow): childRow is number => typeof childRow === 'number')
        .map((childRow) => excelRef(childRow, periodStartCol + columnIndex * (includePercent ? 2 : 1)))
      if (valuesBlank) return null
      if (column.kind !== 'value') {
        const currentColumnIndex = valueColumnIndexFor(column.currentIndices)
        const priorColumnIndex = valueColumnIndexFor(column.priorIndices)
        if (currentColumnIndex < 0 || priorColumnIndex < 0) return null
        const currentRef = excelRef(outputRow, periodStartCol + currentColumnIndex * (includePercent ? 2 : 1))
        const priorRef = excelRef(outputRow, periodStartCol + priorColumnIndex * (includePercent ? 2 : 1))
        if (column.kind === 'pct') {
          const value = Math.abs(sourceValueAt(sourceIndex, priorColumnIndex) || 0) < 0.005
            ? null
            : sourceValue
          return { formula: `=IFERROR((${currentRef}-${priorRef})/ABS(${priorRef}),"")`, value }
        }
        return { formula: `=${currentRef}-${priorRef}`, value: sourceValue / divisor }
      }
      const sourcePeriodIndex = column.indices?.length === 1 ? column.indices[0] : -1
      const cashPlug = sourcePeriodIndex >= 0 ? cashPlugFormula(sourceIndex, sourcePeriodIndex) : null
      if (cashPlug) return cashPlug
      const governedFormula = governedFormulaForColumn(row as GovernedStatementRow, columnIndex, sourceValue)
      if (governedFormula) return governedFormula
      const semanticFormula = governedSourceOnly ? null : formulaForSemantic(semantic, columnIndex)
      if (semanticFormula) return { formula: semanticFormula, value: (valueForSemanticFormula(semantic, columnIndex) ?? sourceValue) / divisor }
      if (!governedSourceOnly && childRefs.length && (rowType === 'group' || rowType === 'formula' || !row.account_id)) {
        const childValue = formulaIndexes.reduce((sum, childIndex) => sum + (sourceValueAt(childIndex, columnIndex) || 0), 0)
        return { formula: sumRefs(childRefs), value: childValue / divisor }
      }
      if (sourcePeriodIndex >= 0 && isEditableForecastLeaf(row, sourceIndex, formulaIndexes, sourcePeriodIndex)) return sourceValue / divisor
      return { formula: sourceFormulaForColumn(sourceRow, column), value: sourceValue / divisor }
    })
    const displayedValues = rowFormulaRefs.map((entry) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return 0
      const value = (entry as { value?: unknown }).value
      return typeof value === 'number' && Number.isFinite(value) ? value : 0
    })
    displayedValuesByOutputRow.set(outputRow, displayedValues)
    const line: SheetCell[] = [rowLabel(row, formatting)]
    for (let columnIndex = 0; columnIndex < columnSpecs.length; columnIndex += 1) {
      line.push(rowFormulaRefs[columnIndex])
      if (includePercent) {
        const amountCol = periodStartCol + columnIndex * 2
        const revenueRow = revenueIndex >= 0 ? dataStartRow + revenueIndex : null
        line.push(revenueRow == null || valuesBlank
          ? null
          : { formula: `=IFERROR(${excelRef(outputRow, amountCol)}/${excelRef(revenueRow, amountCol, true)},"")`, value: null })
      }
    }
    if (includeTotal) {
      if (valuesBlank) {
        line.push(null)
      } else if (
        spec.type === 'balance_sheet'
        || (spec.type === 'cash_flow' && (semantic === 'beginning_cash' || semantic === 'ending_cash'))
      ) {
        // Balance-sheet values and ending cash are point-in-time balances, so
        // their horizon total is the final period. A cash-flow horizon begins
        // with the first period's opening cash and ends with the final period's
        // closing cash; summing either balance (or taking the final opening
        // balance) breaks the aggregate rollforward.
        const pointInTimeIndex = spec.type === 'cash_flow' && semantic === 'beginning_cash'
          ? 0
          : Math.max(0, columnSpecs.length - 1)
        const pointInTimeCol = periodStartCol + pointInTimeIndex * (includePercent ? 2 : 1)
        const pointInTimeValue = rowFormulaRefs[pointInTimeIndex] ?? null
        const computedPointInTimeValue = typeof pointInTimeValue === 'number'
          ? pointInTimeValue
          : pointInTimeValue && typeof pointInTimeValue === 'object' && 'value' in pointInTimeValue
            ? pointInTimeValue.value ?? null
            : null
        line.push({ formula: `=${excelRef(outputRow, pointInTimeCol)}`, value: computedPointInTimeValue })
      } else {
        const refs = columnSpecs.map((_, columnIndex) => excelRef(outputRow, periodStartCol + columnIndex * (includePercent ? 2 : 1)))
        const totalValue = statementRowValues(row).reduce((sum, value) => sum + (Number(value) || 0), 0) / divisor
        line.push({ formula: sumRefs(refs), value: totalValue })
      }
    }
    out.push(line)
    if (semantic && !semanticRows.has(semantic)) semanticRows.set(semantic, outputRow)
    if (semantic && !displayedValuesBySemantic.has(semantic)) displayedValuesBySemantic.set(semantic, displayedValues)
    if (row.node_id) nodeRows.set(String(row.node_id), outputRow)
  })

  const mergeRanges = formatting.showHeaders ? {
    title_company: { sheet_id: sheetId, start_row: 0, start_col: 0, end_row: 0, end_col: Math.max(0, colCount - 1) },
    title_statement: { sheet_id: sheetId, start_row: 1, start_col: 0, end_row: 1, end_col: Math.max(0, colCount - 1) },
    title_meta: { sheet_id: sheetId, start_row: 2, start_col: 0, end_row: 2, end_col: Math.max(0, colCount - 1) },
  } : undefined
  const rowMetadata: Record<string, Record<string, unknown>> = {}
  rows.forEach((row, index) => {
    const rowIndex = dataStartRow + index
    const level = Math.max(0, Math.min(7, Math.floor(Number(row.level || 0))))
    rowMetadata[String(rowIndex)] = {
      outline_level: level,
      height_px: String(row.row_type || '') === 'spacer' ? 8 : 24,
    }
  })
  const sheet: SpreadsheetSheetSnapshot = {
    sheet_id: sheetId,
    name: spec.sheetName,
    row_count: Math.max(out.length + 5, 40),
    col_count: Math.max(colCount, 8),
    chunks: buildChunks(sheetId, out, (row, col) => statementStyleForCell(
      out,
      rows,
      dataStartRow,
      includePercent,
      includeTotal,
      columnSpecs.map((column) => column.kind),
      formatting,
      row,
      col,
    )),
    row_metadata: rowMetadata,
    column_metadata: buildStatementColumnMetadata(colCount, includePercent),
    frozen_panes: { rows: dataStartRow + 1, cols: 1 },
    tab_color: { rgb: spec.tabColor },
    sheet_format_properties: {
      default_row_height_px: 24,
      default_col_width_px: 100,
      outline_level_row: 7,
      outline_summary_below: true,
      show_outline_symbols: true,
      apply_outline_styles: true,
    },
    merge_ranges: mergeRanges,
    print_settings: buildPrintSettings(spec.title),
  }
  return {
    built: { sheet },
    meta: {
      sheetName: spec.sheetName,
      sourceSheetName: spec.sourceSheetName,
      dataStartRow,
      periodStartCol,
      amountCols: columnSpecs.map((_, index) => periodStartCol + index * (includePercent ? 2 : 1)),
      columnKinds: columnSpecs.map((column) => column.kind),
      sourcePeriodStartCol,
      divisor,
      periods: columnSpecs.map((column) => column.label),
      rowBySemantic: semanticRows,
      rowByNodeId: nodeRows,
      sourceIndexBySemantic: semanticSourceIndexes,
      sourceIndexByOutputRow: new Map(Array.from(outputRowBySourceIndex.entries()).map(([sourceIndex, outputRow]) => [outputRow, sourceIndex])),
      displayedValuesBySemantic,
      displayedValuesByOutputRow,
      rows,
    },
  }
}

const statementStyleForCell = (
  matrix: SheetCell[][],
  sourceRows: QuickBooksFpaStatementRow[],
  dataStartRow: number,
  includePercent: boolean,
  includeTotal: boolean,
  columnKinds: CfoStatementColumnSpec['kind'][],
  formatting: Required<QuickBooksFpaStatementFormatting>,
  row: number,
  col: number,
): string | null => {
  if (row === 0) return 'title_company'
  if (row === 1) return 'title_statement'
  if (row === 2) return 'title_meta'
  if (row === dataStartRow - 1) return 'statement_header'
  const sourceRow = sourceRows[row - dataStartRow]
  if (!sourceRow) return col === 0 ? 'statement_label' : 'statement_amount'
  if (String(sourceRow.row_type || '') === 'heading') return 'section_heading'
  const projectedKind = !includePercent && col > 0 ? columnKinds[col - 1] : null
  if (sourceRow.is_total || /^total\b/i.test(String(sourceRow.label || ''))) {
    if (col === 0) return 'total_label'
    return projectedKind === 'pct' ? 'statement_percent' : 'total_amount'
  }
  if (col === 0) return `label_indent_${Math.max(0, Math.min(6, Math.floor(Number(sourceRow.level || 0))))}`
  const lastCol = Math.max(0, (matrix[row]?.length || 1) - 1)
  if (includeTotal && col === lastCol) return 'statement_total_amount'
  if (includePercent && col > 0 && col % 2 === 0) return 'statement_percent'
  if (projectedKind === 'pct') return 'statement_percent'
  return 'statement_amount'
}

const buildStatementColumnMetadata = (colCount: number, includePercent: boolean): Record<string, Record<string, unknown>> => {
  const out: Record<string, Record<string, unknown>> = { '0': { width_px: 280 } }
  for (let col = 1; col < colCount; col += 1) {
    out[String(col)] = { width_px: includePercent && col % 2 === 0 ? 82 : 108 }
  }
  return out
}

const buildPrintSettings = (title: string): SpreadsheetSheetSnapshot['print_settings'] => ({
  print_options: { horizontal_centered: true, grid_lines: false, headings: false },
  page_margins: { left: 0.35, right: 0.35, top: 0.55, bottom: 0.55, header: 0.25, footer: 0.25 },
  page_setup: { orientation: 'landscape', fit_to_width: 1, fit_to_height: 0, paper_size: 9, horizontal_dpi: 300, vertical_dpi: 300, errors: 'dash' },
  header_footer: { odd_header: `&C${title}`, odd_footer: '&RPage &P of &N' },
})

const buildChecksSheet = (
  metas: Record<CfoCoreExcelStatementType, StatementBuildMeta>,
): BuiltSheet => {
  const sheetId = cleanSheetId('Checks')
  const baseMeta = metas.balance_sheet.periods.length ? metas.balance_sheet : metas.profit_and_loss
  const checkedColumnIndexes = baseMeta.columnKinds
    .map((kind, index) => kind === 'pct' ? -1 : index)
    .filter((index) => index >= 0)
  const periods = checkedColumnIndexes.map((index) => baseMeta.periods[index] || '')
  const rows: SheetCell[][] = [
    ['CFO Core Export Checks', ...new Array(Math.max(0, periods.length)).fill(null)],
    ['Check', ...periods.map(formatPeriodLabel), 'Status'],
  ]
  const valuesForSemantic = (meta: StatementBuildMeta, semantic: string): number[] => {
    return meta.displayedValuesBySemantic.get(semantic) || []
  }
  const valuesForOutputRows = (meta: StatementBuildMeta, outputRows: number[]): number[] => checkedColumnIndexes.map((columnIndex) => (
    outputRows.reduce((sum, outputRow) => {
      const values = meta.displayedValuesByOutputRow.get(outputRow)
      return sum + (values?.[columnIndex] ?? 0)
    }, 0)
  ))
  const checks: Array<{ label: string; formulas: string[]; values: number[] }> = []
  const bsAssets = metas.balance_sheet.rowBySemantic.get('total_assets')
  const bsLiabEquity = metas.balance_sheet.rowBySemantic.get('total_liabilities_equity')
  if (bsAssets != null && bsLiabEquity != null) {
    const assetValues = valuesForSemantic(metas.balance_sheet, 'total_assets')
    const liabEquityValues = valuesForSemantic(metas.balance_sheet, 'total_liabilities_equity')
    checks.push({
      label: 'Balance Sheet: Total Assets - Total Liabilities & Equity',
      formulas: checkedColumnIndexes.map((columnIndex) => `=${formulaRef('Balance Sheet', bsAssets, metas.balance_sheet.amountCols[columnIndex])}-${formulaRef('Balance Sheet', bsLiabEquity, metas.balance_sheet.amountCols[columnIndex])}`),
      values: checkedColumnIndexes.map((columnIndex) => (assetValues[columnIndex] ?? 0) - (liabEquityValues[columnIndex] ?? 0)),
    })
  }
  const cfBeginning = metas.cash_flow.rowBySemantic.get('beginning_cash')
  const cfChange = metas.cash_flow.rowBySemantic.get('net_change_cash')
  const cfEnding = metas.cash_flow.rowBySemantic.get('ending_cash')
  if (cfBeginning != null && cfChange != null && cfEnding != null) {
    const beginningValues = valuesForSemantic(metas.cash_flow, 'beginning_cash')
    const changeValues = valuesForSemantic(metas.cash_flow, 'net_change_cash')
    const endingValues = valuesForSemantic(metas.cash_flow, 'ending_cash')
    checks.push({
      label: 'Cash Flow: Beginning Cash + Net Change - Ending Cash',
      formulas: checkedColumnIndexes.map((columnIndex) => `=${formulaRef('Cash Flow', cfBeginning, metas.cash_flow.amountCols[columnIndex])}+${formulaRef('Cash Flow', cfChange, metas.cash_flow.amountCols[columnIndex])}-${formulaRef('Cash Flow', cfEnding, metas.cash_flow.amountCols[columnIndex])}`),
      values: checkedColumnIndexes.map((columnIndex) => (beginningValues[columnIndex] ?? 0) + (changeValues[columnIndex] ?? 0) - (endingValues[columnIndex] ?? 0)),
    })
  }
  const balanceSheetRowsByNodeId = new Map(metas.balance_sheet.rows
    .filter((row) => row.node_id)
    .map((row) => [String(row.node_id), row]))
  const hasCashAncestor = (row: QuickBooksFpaStatementRow): boolean => {
    let parentId = String(row.parent_node_id || '').trim()
    const visited = new Set<string>()
    while (parentId && !visited.has(parentId)) {
      visited.add(parentId)
      const parent = balanceSheetRowsByNodeId.get(parentId)
      if (!parent) return false
      if (isBalanceSheetCashRow(parent)) return true
      parentId = String(parent.parent_node_id || '').trim()
    }
    return false
  }
  // The exact compiler totals each cash-classified hierarchy once: a cash row
  // is included only when no cash-classified ancestor already contains it.
  // Mirroring that rule prevents parent bank groups and their child accounts
  // from being double-counted in the visible cash tie-out formula.
  const bsCashRows = metas.balance_sheet.rows
    .map((row) => ({ row, outputRow: row.node_id ? metas.balance_sheet.rowByNodeId.get(String(row.node_id)) : undefined }))
    .filter(({ row, outputRow }) => outputRow != null && isBalanceSheetCashRow(row) && !hasCashAncestor(row))
    .map(({ outputRow }) => outputRow as number)
  const explicitBsCash = metas.balance_sheet.rowBySemantic.get('cash')
  const explicitBsCashSource = metas.balance_sheet.rows.find((row) => semanticKey(row) === 'cash')
  const explicitBsCashIsGovernedTotal = explicitBsCash != null && explicitBsCashSource != null && (
    explicitBsCashSource.is_total
    || explicitBsCashSource.is_formula
  )
  const cashRows = explicitBsCashIsGovernedTotal
    ? [explicitBsCash]
    : bsCashRows.length
      ? Array.from(new Set(bsCashRows))
      : explicitBsCash != null ? [explicitBsCash] : []
  if (cashRows.length && cfEnding != null) {
    const endingValues = valuesForSemantic(metas.cash_flow, 'ending_cash')
    const cashValues = valuesForOutputRows(metas.balance_sheet, cashRows)
    checks.push({
      label: 'Cash Flow: Ending Cash - Balance Sheet Cash',
      formulas: checkedColumnIndexes.map((columnIndex) => {
        const cashRefs = cashRows.map((row) => formulaRef('Balance Sheet', row, metas.balance_sheet.amountCols[columnIndex]))
        return `=${formulaRef('Cash Flow', cfEnding, metas.cash_flow.amountCols[columnIndex])}-${sumExpression(cashRefs)}`
      }),
      values: checkedColumnIndexes.map((columnIndex, index) => (endingValues[columnIndex] ?? 0) - (cashValues[index] ?? 0)),
    })
  }
  checks.forEach((check) => {
    const nextRowIndex = rows.length
    const formulas = check.formulas.map((formula, index) => ({ formula, value: check.values[index] ?? 0 }))
    const checkRefs = periods.map((_, index) => excelRef(nextRowIndex, index + 1))
    const status = check.values.every((value) => Math.abs(Number(value) || 0) < 0.01) ? 'OK' : 'Review'
    rows.push([
      check.label,
      ...formulas,
      { formula: `=IF(MAX(${checkRefs.map((ref) => `ABS(${ref})`).join(',')})<0.01,"OK","Review")`, value: status },
    ])
  })
  if (!checks.length) rows.push(['No canonical statement check rows were found in this export.', ...new Array(periods.length + 1).fill(null)])
  const rowCount = Math.max(rows.length + 5, 20)
  const colCount = Math.max(periods.length + 2, 8)
  const range: SpreadsheetRangeAddress = { sheet_id: sheetId, start_row: 2, start_col: 1, end_row: Math.max(2, rows.length - 1), end_col: Math.max(1, periods.length) }
  const conditionalFormats: Record<string, Record<string, unknown>> = checks.length ? {
    cfo_checks_negative: {
      id: 'cfo_checks_negative',
      ranges: [range],
      priority: 1,
      stop_if_true: false,
      kind: 'cellIs',
      operator: 'lessThanOrEqual',
      formula1: '-0.01',
      formula2: null,
      format: {
        font: { color: '#991B1B', bold: true },
        fill: { kind: 'solid', foregroundColor: '#FEE2E2', backgroundColor: '#FEE2E2' },
      },
    },
    cfo_checks_positive: {
      id: 'cfo_checks_positive',
      ranges: [range],
      priority: 2,
      stop_if_true: false,
      kind: 'cellIs',
      operator: 'greaterThanOrEqual',
      formula1: '0.01',
      formula2: null,
      format: {
        font: { color: '#991B1B', bold: true },
        fill: { kind: 'solid', foregroundColor: '#FEE2E2', backgroundColor: '#FEE2E2' },
      },
    },
  } : {}
  return {
    sheet: {
      sheet_id: sheetId,
      name: 'Checks',
      row_count: rowCount,
      col_count: colCount,
      chunks: buildChunks(sheetId, rows, (row, col) => {
        if (row === 0) return 'title_statement'
        if (row === 1) return 'statement_header'
        if (col === 0) return 'statement_label'
        if (col === periods.length + 1) return 'check_status'
        return 'statement_amount'
      }),
      frozen_panes: { rows: 2, cols: 1 },
      tab_color: { rgb: '#DC2626' },
      column_metadata: Object.fromEntries(Array.from({ length: colCount }, (_, index) => [
        String(index),
        { width_px: index === 0 ? 390 : index === periods.length + 1 ? 90 : 108 },
      ])),
      print_settings: buildPrintSettings('CFO Core Export Checks'),
    },
    conditionalFormats,
  }
}

const editableAssumptionValue = (value: unknown): SheetCell => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const direct = (value as { value?: unknown }).value
    if (typeof direct === 'number' && Number.isFinite(direct)) return direct
    if (typeof direct === 'string' && direct.trim()) {
      const parsed = Number(direct)
      if (Number.isFinite(parsed)) return parsed
      return direct
    }
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') return value
  return ''
}

const authoredAssumptionValueFormula = (assumption: CfoCorePlanExcelAssumption): string => {
  const metadata = assumption.metadata && typeof assumption.metadata === 'object' ? (assumption.metadata as any) : null
  const formula = String(metadata?.planning_grid?.authored_formulas?.value ?? '').trim()
  return formula.startsWith('=') ? formula : ''
}

const stringifyJson = (value: unknown): string => {
  if (value == null) return ''
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

const buildAssumptionsSheet = (
  assumptions: CfoCorePlanExcelAssumption[] | null | undefined,
  args: { companyName?: string | null; planName?: string | null },
): BuiltSheet => {
  const sheetId = cleanSheetId('Assumptions')
  const rows: SheetCell[][] = [
    [args.companyName || 'Company', null, null, null, null, null],
    [args.planName || 'Forecast Assumptions', null, null, null, null, null],
    ['Edit the Value column, then recalculate the workbook. Forecast statement cells can also be edited directly for fixed-value rows.', null, null, null, null, null],
    [null, null, null, null, null, null],
    ['Key', 'Label', 'Value', 'Source', 'Confidence', 'Evidence / Notes'],
    ...(assumptions || []).map((assumption) => [
      assumption.key || '',
      assumption.label || '',
      authoredAssumptionValueFormula(assumption) || editableAssumptionValue(assumption.value_json),
      assumption.source || '',
      Number.isFinite(Number(assumption.confidence)) ? Number(assumption.confidence) : '',
      stringifyJson(assumption.citation_json || assumption.value_json),
    ]),
  ]
  const colCount = 6
  return {
    sheet: {
      sheet_id: sheetId,
      name: 'Assumptions',
      row_count: Math.max(rows.length + 10, 30),
      col_count: colCount,
      chunks: buildChunks(sheetId, rows, (row, col) => {
        if (row === 0) return 'title_company'
        if (row === 1) return 'title_statement'
        if (row === 2) return 'title_meta'
        if (row === 4) return 'statement_header'
        if (row > 4 && col === 2) return 'assumption_input'
        if (row > 4 && col === 4) return 'assumption_percent'
        return col === 2 ? 'statement_amount' : 'statement_label'
      }),
      frozen_panes: { rows: 5, cols: 0 },
      tab_color: { rgb: '#059669' },
      column_metadata: {
        '0': { width_px: 170 },
        '1': { width_px: 260 },
        '2': { width_px: 115 },
        '3': { width_px: 130 },
        '4': { width_px: 95 },
        '5': { width_px: 420 },
      },
      merge_ranges: {
        assumptions_company: { sheet_id: sheetId, start_row: 0, start_col: 0, end_row: 0, end_col: colCount - 1 },
        assumptions_title: { sheet_id: sheetId, start_row: 1, start_col: 0, end_row: 1, end_col: colCount - 1 },
        assumptions_note: { sheet_id: sheetId, start_row: 2, start_col: 0, end_row: 2, end_col: colCount - 1 },
      },
      print_settings: buildPrintSettings('Forecast Assumptions'),
    },
  }
}

const registerStyles = (builder: WorkbookBuilder, formatting: Required<QuickBooksFpaStatementFormatting>): void => {
  const amount = { numberFormat: { formatCode: amountFormatCode(formatting) }, align: 'right', verticalAlign: 'middle' }
  addStyle(builder, 'title_company', { bold: true, fontSize: 16, textColor: '#0F172A', align: 'left', verticalAlign: 'middle' })
  addStyle(builder, 'title_statement', { bold: true, fontSize: 14, textColor: '#111827', align: 'left', verticalAlign: 'middle' })
  addStyle(builder, 'title_meta', { fontSize: 10, textColor: '#64748B', align: 'left', verticalAlign: 'middle' })
  addStyle(builder, 'statement_header', { bold: true, textColor: '#334155', backgroundColor: '#F8FAFC', align: 'right', verticalAlign: 'middle', border: { bottom: { style: 'thin', color: '#CBD5E1' } } })
  addStyle(builder, 'statement_label', { textColor: '#0F172A', align: 'left', verticalAlign: 'middle' })
  addStyle(builder, 'statement_amount', amount)
  addStyle(builder, 'statement_total_amount', { ...amount, bold: true, backgroundColor: '#F8FAFC' })
  addStyle(builder, 'statement_percent', { numberFormat: { formatCode: percentFormatCode(formatting) }, align: 'right', textColor: '#047857' })
  addStyle(builder, 'section_heading', { bold: true, textColor: '#0F172A', backgroundColor: '#ECFDF5', align: 'left', verticalAlign: 'middle' })
  addStyle(builder, 'total_label', { bold: true, textColor: '#0F172A', backgroundColor: '#F8FAFC', border: { top: { style: 'thin', color: '#64748B' } } })
  addStyle(builder, 'total_amount', { ...amount, bold: true, backgroundColor: '#F8FAFC', border: { top: { style: 'thin', color: '#64748B' } } })
  addStyle(builder, 'source_header', { bold: true, textColor: '#334155', backgroundColor: '#E2E8F0', align: 'left' })
  addStyle(builder, 'source_text', { textColor: '#334155', align: 'left' })
  addStyle(builder, 'source_amount', { numberFormat: { formatCode: '#,##0.00;(#,##0.00);-' }, align: 'right' })
  addStyle(builder, 'check_status', { bold: true, align: 'center' })
  addStyle(builder, 'assumption_input', { ...amount, backgroundColor: '#FEFCE8', border: { bottom: { style: 'thin', color: '#FACC15' } } })
  addStyle(builder, 'assumption_percent', { numberFormat: { formatCode: '0.0%' }, align: 'right', verticalAlign: 'middle' })
  for (let level = 0; level <= 6; level += 1) {
    addStyle(builder, `label_indent_${level}`, { textColor: '#0F172A', align: 'left', verticalAlign: 'middle', indent: level })
  }
}

export const buildCfoCoreThreeStatementExcelSnapshot = (input: CfoCoreExcelExportInput): SpreadsheetWorkbookSnapshot => {
  const formatting = normalizeFormatting(input.formatting)
  const builder: WorkbookBuilder = { styles: {} }
  registerStyles(builder, formatting)
  const sheets: SpreadsheetSheetSnapshot[] = []
  const conditionalFormats: Record<string, Record<string, unknown>> = {}
  const metas = {} as Record<CfoCoreExcelStatementType, StatementBuildMeta>
  for (const spec of STATEMENT_SPECS) {
    const render = input.statements[spec.type]
    const statement = buildStatementSheet(builder, spec, render, formatting, input.hideZeroRows, {
      companyName: input.companyName,
      currencyCode: input.currencyCode,
      asOfDate: input.asOfDate,
      grain: input.grain,
      view: input.view,
    })
    sheets.push(statement.built.sheet)
    metas[spec.type] = statement.meta
  }
  for (const spec of STATEMENT_SPECS) {
    sheets.push(buildSourceSheet(spec, {
      ...input.statements[spec.type],
      rows: filterZeroStatementRows(input.statements[spec.type].rows || [], input.hideZeroRows),
    }).sheet)
  }
  const checks = buildChecksSheet(metas)
  sheets.push(checks.sheet)
  Object.assign(conditionalFormats, checks.conditionalFormats || {})
  return {
    protocol_version: SPREADSHEET_ENGINE_PROTOCOL_VERSION,
    workbook_id: `cfo_core_export_${Date.now().toString(36)}`,
    workbook_revision: 1,
    workbook_properties: { date_1904: false },
    document_properties: {
      title: `${input.companyName || 'CFO Core'} 3 Statement Export`,
      creator: 'FuseGrid CFO Core',
      company: input.companyName || undefined,
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
    },
    calculation_settings: {
      calc_mode: 'auto',
      full_calc_on_load: true,
      force_full_calc: true,
    },
    sheets,
    sheet_order: sheets.map((sheet) => sheet.sheet_id),
    named_ranges: {},
    validation_rules: {},
    styles: builder.styles,
    conditional_formats: conditionalFormats,
    sparkline_groups: {},
    derived_artifacts: {},
    external_sources: {},
  }
}

export const buildCfoCorePlanExcelSnapshot = (input: CfoCorePlanExcelExportInput): SpreadsheetWorkbookSnapshot => {
  const formatting = normalizeFormatting(input.formatting)
  const builder: WorkbookBuilder = { styles: {} }
  registerStyles(builder, formatting)
  const sheets: SpreadsheetSheetSnapshot[] = []
  const conditionalFormats: Record<string, Record<string, unknown>> = {}
  const metas = {} as Record<CfoCoreExcelStatementType, StatementBuildMeta>
  sheets.push(buildAssumptionsSheet(input.assumptions, {
    companyName: input.companyName,
    planName: input.planName,
  }).sheet)
  for (const spec of STATEMENT_SPECS) {
    const render = input.statements[spec.type]
    const statement = buildStatementSheet(builder, spec, render, formatting, input.hideZeroRows, {
      companyName: input.companyName,
      currencyCode: input.currencyCode,
      asOfDate: input.asOfDate,
      grain: input.grain,
      periodKinds: input.periodKinds?.[spec.type] || null,
      editableForecastCells: true,
    })
    sheets.push(statement.built.sheet)
    metas[spec.type] = statement.meta
  }
  for (const spec of STATEMENT_SPECS) {
    sheets.push(buildSourceSheet(spec, {
      ...input.statements[spec.type],
      rows: filterZeroStatementRows(input.statements[spec.type].rows || [], input.hideZeroRows),
    }).sheet)
  }
  const checks = buildChecksSheet(metas)
  sheets.push(checks.sheet)
  Object.assign(conditionalFormats, checks.conditionalFormats || {})
  return {
    protocol_version: SPREADSHEET_ENGINE_PROTOCOL_VERSION,
    workbook_id: `cfo_core_plan_export_${Date.now().toString(36)}`,
    workbook_revision: 1,
    workbook_properties: { date_1904: false },
    document_properties: {
      title: `${input.companyName || 'CFO Core'} ${input.planName || 'Forecast'} Export`,
      creator: 'FuseGrid CFO Core',
      company: input.companyName || undefined,
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
    },
    calculation_settings: {
      calc_mode: 'auto',
      full_calc_on_load: true,
      force_full_calc: true,
    },
    sheets,
    sheet_order: sheets.map((sheet) => sheet.sheet_id),
    named_ranges: {},
    validation_rules: {},
    styles: builder.styles,
    conditional_formats: conditionalFormats,
    sparkline_groups: {},
    derived_artifacts: {},
    external_sources: {},
  }
}

export const buildCfoCoreExcelFileName = (companyName?: string | null, asOfDate?: string | null): string => {
  const company = String(companyName || 'CFO Core').trim().replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, ' ') || 'CFO Core'
  const date = String(asOfDate || '').trim() || new Date().toISOString().slice(0, 10)
  return `${company} CFO Core 3 Statement ${date}.xlsx`
}
